import { VariantType } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { SWRConfiguration } from 'swr';

import { getCurrentLanguage } from '~/localization';
import {
  ApprovalWidgetCategory,
  BulkApprovalAction,
  BulkApprovalStatus,
  approveRewardingProposal,
} from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/data';
import {
  ApprovalWidgetData,
  ProposalDto,
} from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/types';
import { RelationsDto } from '~/services/Http/client.people.generated';
import {
  AllowanceDto,
  BenefitDto,
  IncentiveDto,
  SalaryDto,
  WorkflowStatuses2,
} from '~/services/Http/client.rewarding.generated';
import { useGetAllowanceProposals } from '~/services/Http/hooks/people/useGetAllowanceProposals';
import { useGetBenefitProposals } from '~/services/Http/hooks/people/useGetBenefitProposals';
import { useGetIncentiveProposals } from '~/services/Http/hooks/people/useGetIncentiveProposals';
import { useGetRelations } from '~/services/Http/hooks/people/useGetRelations';
import { useGetSalaryProposals } from '~/services/Http/hooks/people/useGetSalaryProposals';
import { isHR } from '~/services/Permissions';
import { browserLogError } from '~/utils/helper';

type Props = {
  employeeId: number;
  userRoles: string[];
  widgetRoles?: string[];
  enabled: boolean;
  showSnackBar: (
    responsesNumber: number,
    variant: VariantType,
    action: BulkApprovalAction,
  ) => void;
};

export type ApprovalWidgetRewardingDataDetailedState = {
  allowance: boolean;
  benefit: boolean;
  incentive: boolean;
  salary: boolean;
};

type UseApprovalWidgetRewardingDataOutput = {
  isLoading: boolean;
  isLoadingDetailed: ApprovalWidgetRewardingDataDetailedState;
  failedToLoad: boolean;
  failedToLoadDetailed: ApprovalWidgetRewardingDataDetailedState;
  data: {
    allowance: ApprovalWidgetData[];
    benefit: ApprovalWidgetData[];
    incentive: ApprovalWidgetData[];
    salary: ApprovalWidgetData[];
  };
  onDecision: (
    action: BulkApprovalAction,
    category: ApprovalWidgetCategory,
  ) => Promise<void>;
  selection: {
    allowance: ApprovalWidgetData[];
    benefit: ApprovalWidgetData[];
    incentive: ApprovalWidgetData[];
    salary: ApprovalWidgetData[];
  };
  setSelection: (
    category: ApprovalWidgetCategory,
    selection: ApprovalWidgetData[],
  ) => void;
};

export const useApprovalWidgetRewardingData = (
  props: Props,
  swrConfig?: SWRConfiguration,
): UseApprovalWidgetRewardingDataOutput => {
  const { employeeId, userRoles, enabled, showSnackBar, widgetRoles } = props;

  const workflowStatuses = isHR(userRoles)
    ? 'PROPOSED,MANAGER_APPROVED'
    : 'PROPOSED';

  const commonRewardingRequestProps = {
    roles: widgetRoles,
    workflowStatuses: workflowStatuses.split(',') as WorkflowStatuses2[],
    workflowHandlers: [employeeId],
  };

  const returnIds = (
    allowanceProposals: AllowanceDto[],
    benefitProposals: BenefitDto[],
    incentiveProposals: IncentiveDto[],
    salaryProposals: SalaryDto[],
  ): { employeeIds: number[]; employmentIds: number[] } => {
    const data = [
      ...allowanceProposals,
      ...benefitProposals,
      ...incentiveProposals,
      ...salaryProposals,
    ];
    const employeeIds: number[] = data.map((proposal) => proposal.employeeId);
    const employmentIds: number[] = data.map(
      (proposal) => proposal.employmentId,
    );

    return { employeeIds, employmentIds };
  };

  const {
    data: allowanceProposals,
    loading: allowanceProposalsLoading,
    failedToLoad: allowanceProposalsFailedToLoad,
    mutate: mutateAllowance,
  } = useGetAllowanceProposals(
    commonRewardingRequestProps,
    swrConfig,
    !enabled,
  );
  const {
    data: benefitProposals,
    loading: benefitProposalsLoading,
    failedToLoad: benefitProposalsFailedToLoad,
    mutate: mutateBenefit,
  } = useGetBenefitProposals(commonRewardingRequestProps, swrConfig, !enabled);
  const {
    data: incentiveProposals,
    loading: incentiveProposalsLoading,
    failedToLoad: incentiveProposalsFailedToLoad,
    mutate: mutateIncentive,
  } = useGetIncentiveProposals(
    commonRewardingRequestProps,
    swrConfig,
    !enabled,
  );
  const {
    data: salaryProposals,
    loading: salaryProposalsLoading,
    failedToLoad: salaryProposalsFailedToLoad,
    mutate: mutateSalary,
  } = useGetSalaryProposals(commonRewardingRequestProps, swrConfig, !enabled);

  const { employeeIds, employmentIds } = returnIds(
    allowanceProposals ?? [],
    benefitProposals ?? [],
    incentiveProposals ?? [],
    salaryProposals ?? [],
  );
  const { data: relations, loading: relationsLoading } = useGetRelations(
    {
      lang: getCurrentLanguage('en'),
      employmentIds,
      employeeIds,
    },
    swrConfig,
  );

  const logMissingRelationsData = useCallback(
    (logProps: {
      relations?: RelationsDto;
      proposal:
        | ProposalDto
        | AllowanceDto
        | BenefitDto
        | IncentiveDto
        | SalaryDto;
      proposalsType: string;
    }) => {
      const {
        relations,
        proposal: { id, employeeId, employmentId },
        proposalsType,
      } = logProps;

      if (relationsLoading) return;

      const errors = [];

      if (!relations?.employees[employeeId]) {
        errors.push(`Relation data missing for employee ${employeeId}`);
      }

      if (!relations?.employments[employmentId]) {
        errors.push(`Relation data missing for employment ${employmentId}`);
      }

      if (errors.length > 0) {
        browserLogError([
          `Approval widget: Issue with relations data for ${proposalsType} proposal ${id}`,
          ...errors,
        ]);
      }
    },
    [relationsLoading],
  );

  const allowanceWidgetData: ApprovalWidgetData[] = useMemo(() => {
    const logMissingProposalData = (proposal: AllowanceDto) =>
      logMissingRelationsData({
        relations: relations,
        proposal,
        proposalsType: ApprovalWidgetCategory.Allowance,
      });

    return allowanceProposals
      ? [
          ...allowanceProposals.map(
            (proposal: AllowanceDto): ApprovalWidgetData => {
              logMissingProposalData(proposal);

              return {
                employee: (relations?.employees ?? {})[proposal.employeeId],
                type: proposal.type?.name ?? '',
                startDate: proposal.startDate
                  ? new Date(proposal.startDate).toISOString()
                  : '',
                value: String(proposal.amount),
                currency:
                  relations?.employments[proposal.employmentId].currency.code,
                id: String(proposal.id),
                uid: String(proposal.uid),
                category: proposal.category,
                info: proposal.info || '',
              };
            },
          ),
        ]
      : [];
  }, [allowanceProposals, relations, logMissingRelationsData]);

  const benefitWidgetData: ApprovalWidgetData[] = useMemo(() => {
    const logMissingProposalData = (proposal: BenefitDto) =>
      logMissingRelationsData({
        relations: relations,
        proposal,
        proposalsType: ApprovalWidgetCategory.Benefit,
      });

    return benefitProposals
      ? [
          ...benefitProposals.map(
            (proposal: BenefitDto): ApprovalWidgetData => {
              logMissingProposalData(proposal);

              return {
                employee: (relations?.employees ?? {})[proposal.employeeId],
                type: proposal.type?.name ?? '',
                startDate: proposal.startDate
                  ? new Date(proposal.startDate).toISOString()
                  : '',
                value: String(proposal.amount),
                currency:
                  relations?.employments[proposal.employmentId].currency.code,
                id: String(proposal.id),
                uid: String(proposal.uid),
                category: proposal.category,
                info: proposal.info || '',
              };
            },
          ),
        ]
      : [];
  }, [benefitProposals, relations, logMissingRelationsData]);

  const incentiveWidgetData: ApprovalWidgetData[] = useMemo(() => {
    const logMissingProposalData = (proposal: IncentiveDto) =>
      logMissingRelationsData({
        relations: relations,
        proposal,
        proposalsType: ApprovalWidgetCategory.Incentive,
      });

    return incentiveProposals
      ? [
          ...incentiveProposals.map(
            (proposal: IncentiveDto): ApprovalWidgetData => {
              logMissingProposalData(proposal);

              return {
                employee: (relations?.employees ?? {})[proposal.employeeId],
                type: proposal.type?.name ?? '',
                startDate: proposal.enteredTime
                  ? new Date(proposal.enteredTime).toISOString()
                  : '',
                value: String(proposal.amount),
                currency:
                  relations?.employments[proposal.employmentId].currency.code,
                id: String(proposal.id),
                uid: String(proposal.uid),
                category: proposal.category,
                info: proposal.info || '',
              };
            },
          ),
        ]
      : [];
  }, [incentiveProposals, relations, logMissingRelationsData]);

  const salaryWidgetData: ApprovalWidgetData[] = useMemo(() => {
    const logMissingProposalData = (proposal: SalaryDto) =>
      logMissingRelationsData({
        relations: relations,
        proposal,
        proposalsType: ApprovalWidgetCategory.Salary,
      });

    return salaryProposals
      ? [
          ...salaryProposals.map((proposal: SalaryDto): ApprovalWidgetData => {
            logMissingProposalData(proposal);

            return {
              employee: (relations?.employees ?? {})[proposal.employeeId],
              type: proposal.type?.name ?? '',
              startDate: proposal.startDate
                ? new Date(proposal.startDate).toISOString()
                : '',
              value: String(proposal.amount),
              currency:
                relations?.employments[proposal.employmentId].currency.code,
              id: String(proposal.id),
              uid: String(proposal.uid),
              category: proposal.category,
              info: proposal.info || '',
            };
          }),
        ]
      : [];
  }, [salaryProposals, relations, logMissingRelationsData]);

  const updateCategory = useCallback(
    (category: ApprovalWidgetCategory) => {
      switch (category) {
        case ApprovalWidgetCategory.Allowance:
          return mutateAllowance();
        case ApprovalWidgetCategory.Benefit:
          return mutateBenefit();
        case ApprovalWidgetCategory.Incentive:
          return mutateIncentive();
        case ApprovalWidgetCategory.Salary:
          return mutateSalary();
        default:
          return;
      }
    },
    [mutateAllowance, mutateBenefit, mutateIncentive, mutateSalary],
  );

  // Selection handling
  const [selectedAllowances, setSelectedAllowances] = useState<
    ApprovalWidgetData[]
  >([]);
  const [selectedBenefits, setSelectedBenefits] = useState<
    ApprovalWidgetData[]
  >([]);
  const [selectedIncentives, setSelectedIncentives] = useState<
    ApprovalWidgetData[]
  >([]);
  const [selectedSalaries, setSelectedSalaries] = useState<
    ApprovalWidgetData[]
  >([]);

  const getSelectedByCategory = useCallback(
    (category: ApprovalWidgetCategory) => {
      switch (category) {
        case ApprovalWidgetCategory.Allowance:
          return selectedAllowances;
        case ApprovalWidgetCategory.Benefit:
          return selectedBenefits;
        case ApprovalWidgetCategory.Incentive:
          return selectedIncentives;
        case ApprovalWidgetCategory.Salary:
          return selectedSalaries;
        default:
          return [];
      }
    },
    [
      selectedAllowances,
      selectedBenefits,
      selectedIncentives,
      selectedSalaries,
    ],
  );

  const setSelectedOfCategory = useCallback(
    (category: ApprovalWidgetCategory, selection: ApprovalWidgetData[]) => {
      switch (category) {
        case ApprovalWidgetCategory.Allowance:
          return setSelectedAllowances(selection);
        case ApprovalWidgetCategory.Benefit:
          return setSelectedBenefits(selection);
        case ApprovalWidgetCategory.Incentive:
          return setSelectedIncentives(selection);
        case ApprovalWidgetCategory.Salary:
          return setSelectedSalaries(selection);
        default:
          return;
      }
    },
    [
      setSelectedAllowances,
      setSelectedBenefits,
      setSelectedIncentives,
      setSelectedSalaries,
    ],
  );

  // Decision handling
  const handleRewardingsApprove = useCallback(
    async (action: BulkApprovalAction, data: ApprovalWidgetData[]) => {
      const proposalResponses = await approveRewardingProposal(action, data);
      const failedResponses = proposalResponses.filter(
        (pr) => pr.status === BulkApprovalStatus.FAILED,
      );

      return { proposalResponses, failedResponses };
    },
    [],
  );

  const handleRewardingProposalsDecision = useCallback(
    (action: BulkApprovalAction, category: ApprovalWidgetCategory) => {
      const selected = getSelectedByCategory(category);

      if (!selected) {
        return Promise.resolve();
      }

      return handleRewardingsApprove(action, selected).then(
        ({ proposalResponses, failedResponses }) => {
          const successResponses = proposalResponses.filter(
            (pr) => !failedResponses.some((fr) => fr.uid === pr.uid),
          );

          const failedResponsesNumber = failedResponses.length;
          const successResponsesNumber = successResponses.length;

          setSelectedOfCategory(
            category,
            selected.filter((r) =>
              failedResponses.some((fr) => fr.uid === r.uid),
            ),
          );

          updateCategory(category);

          if (!!successResponsesNumber) {
            showSnackBar(successResponsesNumber, 'success', action);
          }
          if (!!failedResponsesNumber) {
            showSnackBar(failedResponsesNumber, 'error', action);
          }
        },
      );
    },
    [
      getSelectedByCategory,
      setSelectedOfCategory,
      handleRewardingsApprove,
      showSnackBar,
      updateCategory,
    ],
  );

  return {
    isLoading:
      enabled &&
      (allowanceProposalsLoading ||
        benefitProposalsLoading ||
        incentiveProposalsLoading ||
        salaryProposalsLoading),
    isLoadingDetailed: {
      allowance: enabled && allowanceProposalsLoading,
      benefit: enabled && benefitProposalsLoading,
      incentive: enabled && incentiveProposalsLoading,
      salary: enabled && salaryProposalsLoading,
    },
    failedToLoad:
      enabled &&
      (allowanceProposalsFailedToLoad ||
        benefitProposalsFailedToLoad ||
        incentiveProposalsFailedToLoad ||
        salaryProposalsFailedToLoad),
    failedToLoadDetailed: {
      allowance: enabled && allowanceProposalsFailedToLoad,
      benefit: enabled && benefitProposalsFailedToLoad,
      incentive: enabled && incentiveProposalsFailedToLoad,
      salary: enabled && salaryProposalsFailedToLoad,
    },
    data: {
      allowance: allowanceWidgetData,
      benefit: benefitWidgetData,
      incentive: incentiveWidgetData,
      salary: salaryWidgetData,
    },
    onDecision: handleRewardingProposalsDecision,
    selection: {
      allowance: selectedAllowances,
      benefit: selectedBenefits,
      incentive: selectedIncentives,
      salary: selectedSalaries,
    },
    setSelection: setSelectedOfCategory,
  };
};
